import { absolutize, ENDPOINTS } from '../lib/api.js';
import { useCmsList } from '../hooks/useCmsList.js';
import { TRUST } from '../content/home.js';
import AccentedHeading from '../components/ui/Accent.jsx';

/**
 * Number of logos at which a scrolling marquee starts looking better than a
 * static row.
 *
 * Below this, the loop is visibly short — the same logo reappears every few
 * seconds, which reads as "we only have three customers" more loudly than
 * simply showing three logos would. So the component switches presentation
 * automatically as logos are added in the CMS; nothing to change in code.
 */
const MARQUEE_THRESHOLD = 6;

export default function TrustLogos() {
  const { items: logos, loaded } = useCmsList(ENDPOINTS.companies, {
    map: (c) => {
      const src = absolutize(c.company_img);
      return src ? { id: c.id, src, name: c.name ?? null } : null;
    },
  });

  const marquee = logos.length >= MARQUEE_THRESHOLD;

  return (
    <section className="pt-20 pb-12 lg:pt-24">
      <div className="wrap text-center">
        <h2 className="mb-2.5 font-display text-[24px] font-bold text-deep lg:text-[28px]">
          <AccentedHeading text={TRUST.heading} accent={TRUST.accent} on="light" />
        </h2>
        <p className="mx-auto mb-10 max-w-[640px] text-[15px] text-muted">{TRUST.sub}</p>
      </div>

      {logos.length > 0 ? (
        marquee ? <Marquee logos={logos} /> : <StaticRow logos={logos} />
      ) : (
        // Only after the request settles, so the fallback badges don't flash
        // in front of logos that are about to load.
        loaded && <FallbackBadges />
      )}
    </section>
  );
}

/** Fewer than the threshold: a centred, evenly spaced row. */
function StaticRow({ logos }) {
  return (
    <div className="wrap">
      <ul className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 rounded-2xl border border-line bg-white px-6 py-8 sm:gap-x-14">
        {logos.map((logo) => (
          <li key={logo.id}>
            <Logo logo={logo} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * At or above the threshold: a seamless infinite scroll.
 *
 * The list is rendered twice inside one track. The animation shifts the track
 * by exactly -50%, which places the duplicate exactly where the original
 * began — so the restart is invisible. The second copy is aria-hidden, or a
 * screen reader would announce every customer twice.
 */
function Marquee({ logos }) {
  // Keep a constant pace as logos are added, rather than speeding up.
  const duration = Math.max(24, logos.length * 4.5);

  return (
    <div
      className="group relative overflow-hidden border-y border-line bg-white py-8"
      style={{
        // Fades both ends so logos slide out of view instead of being chopped.
        maskImage:
          'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
        WebkitMaskImage:
          'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
      }}
    >
      <ul
        className="flex w-max items-center gap-x-14 pr-14 animate-marquee [animation-play-state:running] group-hover:[animation-play-state:paused] motion-reduce:animate-none motion-reduce:justify-center"
        style={{ '--marquee-duration': `${duration}s` }}
      >
        {logos.map((logo) => (
          <li key={logo.id}>
            <Logo logo={logo} />
          </li>
        ))}
        {logos.map((logo) => (
          <li key={`dup-${logo.id}`} aria-hidden="true" className="motion-reduce:hidden">
            <Logo logo={logo} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Full colour.
 *
 * Greyscaling a logo wall is the reflex, and it is the right call on a site
 * showing thirty logos of household names, where the row is a texture and
 * recognition is already guaranteed. Neither holds here. There are a handful
 * of logos, and East African agri brands are not names a visitor recognises
 * from a grey silhouette — draining the colour costs the only thing the row
 * exists to buy. Muted logos also read as tentative, which is the opposite of
 * what a trust bar is for.
 *
 * The scrappiness greyscale was hiding is handled by normalising height and
 * capping width instead, so the row stays even without flattening the brands.
 */
function Logo({ logo }) {
  return (
    <img
      src={logo.src}
      alt={logo.name ? `${logo.name} logo` : ''}
      loading="lazy"
      decoding="async"
      className="h-10 w-auto max-w-[150px] object-contain transition duration-300 hover:scale-[1.06] sm:h-12"
      onError={(e) => {
        // CMS rows store absolute urls that can point at a dev host.
        e.currentTarget.closest('li')?.remove();
      }}
    />
  );
}

/** Shown only if the CMS is unreachable, so the section is never empty. */
function FallbackBadges() {
  return (
    <div className="wrap">
      <div className="flex flex-wrap items-center justify-center gap-3.5 rounded-2xl border border-line bg-white px-6 py-6">
        {TRUST.badges.map((b) => (
          <span
            key={b}
            className="rounded-lg bg-wash px-5 py-2.5 font-display text-[14px] font-bold tracking-[0.3px] text-field"
          >
            {b}
          </span>
        ))}
        <span className="text-[13.5px] font-medium text-muted">{TRUST.more}</span>
      </div>
    </div>
  );
}
